/**
 * Programmatic specialty × city landing page.
 *
 * Route:  /providers/:specialtySlug-in-:citySlug-:stateCode
 * URL ex: /providers/family-medicine-in-bend-or
 *         /providers/cardiology-in-austin-tx
 *         /providers/pediatrics-in-new-york-city-ny
 *
 * These pages target long-tail local SEO queries like "family medicine in bend or"
 * or "pediatrician in austin tx". They're a near-duplicate of
 * SpecialtyStateDirectory but narrowed to a specific city.
 *
 * The backend `/api/npi/specialty/{specialty}` already accepts a `city` query
 * parameter, so no new backend endpoint is needed.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretLeft, CaretRight, MapPin, Stethoscope, Buildings } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import ProviderCard from "../components/ProviderCard";
import { api } from "../lib/api";
import { richItemListSchema } from "../lib/jsonLd";

const US_STATES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  DC: "District of Columbia",
};

const SPECIALTY_DISPLAY_NAMES = {
  "cardiology": "Cardiology",
  "cardiologist": "Cardiology",
  "dermatology": "Dermatology",
  "dermatologist": "Dermatology",
  "pediatrics": "Pediatrics",
  "pediatrician": "Pediatrics",
  "psychiatry": "Psychiatry",
  "psychiatrist": "Psychiatry",
  "orthopedics": "Orthopedics",
  "orthopedic": "Orthopedics",
  "family-medicine": "Family Medicine",
  "internal-medicine": "Internal Medicine",
  "neurology": "Neurology",
  "neurologist": "Neurology",
  "oncology": "Oncology",
  "oncologist": "Oncology",
  "radiology": "Radiology",
};

const ITEMS_PER_PAGE = 20;

const titleCase = (s) =>
  (s || "")
    .split(/[\s-]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");

const SpecialtyCityDirectory = () => {
  const params = useParams();
  const rawSlug = params.slug || "";

  // Parse slug: `{specialty-slug}-in-{city-slug}-{state-abbr}`
  // Examples:
  //   family-medicine-in-bend-or  → specialty=family-medicine, city=bend,     state=or
  //   cardiology-in-new-york-ny   → specialty=cardiology,      city=new-york, state=ny
  //   pediatrics-in-st-louis-mo   → specialty=pediatrics,      city=st-louis, state=mo
  const slugMatch = rawSlug.match(/^(.+?)-in-(.+)-([a-z]{2})$/i);
  const specialty = slugMatch ? slugMatch[1].toLowerCase() : "";
  const citySlug = slugMatch ? slugMatch[2].toLowerCase() : "";
  const stateCode = slugMatch ? slugMatch[3].toUpperCase() : "";

  const stateName = US_STATES[stateCode] || stateCode;
  const cityName = titleCase(citySlug);
  const specialtyName =
    SPECIALTY_DISPLAY_NAMES[specialty.toLowerCase()] ||
    specialty.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const validRoute = Boolean(stateCode && US_STATES[stateCode] && citySlug && specialty);

  const fetchResults = useCallback(async () => {
    if (!validRoute) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api.getProvidersBySpecialty(specialty, {
        state: stateCode,
        city: cityName,
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
      });
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
      setHasMore(data.has_more || false);
    } catch (e) {
      console.error("SpecialtyCityDirectory fetch error:", e);
      setResults([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [specialty, stateCode, cityName, currentPage, validRoute]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  if (!validRoute) {
    return (
      <div className="min-h-[60vh] bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg border border-slate-200 p-10 max-w-md text-center">
          <h1 className="font-display font-bold text-2xl text-navy mb-2">Page not found</h1>
          <p className="text-slate-500 mb-6">
            The URL format should be like <code>/providers/family-medicine-in-bend-or</code>.
          </p>
          <Link to="/specialties">
            <Button variant="outline">Browse specialties</Button>
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://npilookup.com/providers/${specialty}-in-${citySlug}-${stateCode.toLowerCase()}`;
  const pageTitle = `${specialtyName} in ${cityName}, ${stateCode} — NPI Lookup`;
  const pageDescription =
    `Find ${specialtyName.toLowerCase()} providers in ${cityName}, ${stateCode}. Browse ` +
    `${totalCount > 0 ? totalCount.toLocaleString() + "+" : "verified"} ${specialtyName.toLowerCase()} ` +
    `doctors with NPI numbers from the NPPES national registry. Verify credentials, addresses, ` +
    `and specialties instantly.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: `${specialtyName} in ${cityName}, ${stateCode}`,
            description: pageDescription,
            url: canonicalUrl,
            specialty: { "@type": "MedicalSpecialty", name: specialtyName },
            about: { "@type": "City", name: cityName, containedInPlace: { "@type": "State", name: stateName } },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(richItemListSchema(
            `${specialtyName} Providers in ${cityName}, ${stateCode}`,
            pageDescription,
            results,
            `/providers/${specialty}-in-${citySlug}-${stateCode.toLowerCase()}`,
          ))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://npilookup.com/" },
              { "@type": "ListItem", position: 2, name: specialtyName, item: `https://npilookup.com/specialty/${specialty}` },
              { "@type": "ListItem", position: 3, name: stateName, item: `https://npilookup.com/specialty/${specialty}/${stateCode.toLowerCase()}` },
              { "@type": "ListItem", position: 4, name: `${cityName}, ${stateCode}` },
            ],
          })}
        </script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              <li><Link to={`/specialty/${specialty}`} className="hover:text-teal">{specialtyName}</Link></li>
              <li>/</li>
              <li>
                <Link to={`/specialty/${specialty}/${stateCode.toLowerCase()}`} className="hover:text-teal">
                  {stateName}
                </Link>
              </li>
              <li>/</li>
              <li className="text-slate-900 font-medium">{cityName}</li>
            </ol>
          </nav>

          {/* Header */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-teal" />
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <Buildings className="w-4 h-4" />
                <span className="font-medium">{cityName}, {stateCode}</span>
              </div>
            </div>

            <h1 className="font-display font-bold text-3xl sm:text-4xl text-navy mb-2" data-testid="specialty-city-title">
              {specialtyName} in {cityName}, {stateCode}
            </h1>
            <p className="text-slate-500 text-lg">
              Verified {specialtyName.toLowerCase()} healthcare providers in {cityName}, {stateName}
            </p>

            <div className="mt-4 flex items-center gap-4 flex-wrap">
              <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full" data-testid="specialty-city-count">
                {loading ? "Loading..." : `${totalCount.toLocaleString()} providers found`}
              </span>
            </div>
          </div>

          {/* Related Links */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Link
              to={`/specialty/${specialty}/${stateCode.toLowerCase()}`}
              className="text-sm px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-teal text-slate-600 hover:text-teal transition-colors"
              data-testid="related-state-link"
            >
              All {specialtyName} Providers in {stateName}
            </Link>
            <Link
              to={`/specialty/${specialty}`}
              className="text-sm px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-teal text-slate-600 hover:text-teal transition-colors"
            >
              All {specialtyName} Providers
            </Link>
            <Link
              to={`/state/${stateCode}/${encodeURIComponent(cityName.toLowerCase())}`}
              className="text-sm px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-teal text-slate-600 hover:text-teal transition-colors"
            >
              All Providers in {cityName}
            </Link>
          </div>

          {/* Results */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="skeleton h-4 w-20 mb-3 rounded" />
                  <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/3 mb-4 rounded" />
                  <div className="skeleton h-10 w-full rounded" />
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="specialty-city-empty">
              <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                No providers found
              </h3>
              <p className="text-slate-500 mb-6">
                We couldn't find any {specialtyName.toLowerCase()} providers in {cityName}, {stateCode} right now.
                Try the broader state-level page:
              </p>
              <Link to={`/specialty/${specialty}/${stateCode.toLowerCase()}`}>
                <Button variant="outline">
                  View {specialtyName} in {stateName}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="specialty-city-results-grid"
              >
                {results.map((provider) => (
                  <ProviderCard key={provider.number} provider={provider} />
                ))}
              </div>

              {(hasMore || currentPage > 1) && (
                <div className="flex items-center justify-center gap-3 mt-8" data-testid="specialty-city-pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <CaretLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-slate-500 font-medium px-3">Page {currentPage}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={!hasMore}
                    className="pagination-btn"
                  >
                    Next
                    <CaretRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}

          {/* SEO content */}
          <div className="mt-12 bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="font-display font-semibold text-xl text-slate-900 mb-4">
              About {specialtyName} in {cityName}, {stateCode}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Looking for a {specialtyName.toLowerCase()} doctor in {cityName}, {stateName}? Our directory
              aggregates verified {specialtyName.toLowerCase()} providers from the NPPES national registry —
              the authoritative source maintained by CMS. Each listing includes the provider's NPI number,
              credentials, practice address, and specialty taxonomy. Use NPI Lookup to verify a provider's
              credentials before your appointment, check PECOS enrollment status, or confirm OIG exclusion
              list clearance for credentialing workflows.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpecialtyCityDirectory;
